import React from 'react';
import { useRowLinkBuilder } from '../../../lib/useRowLink';
import type { PagesSearchParams } from '../../../router';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { ColumnDef, RowSelectionState, StockFeatures } from '@tanstack/react-table';
import {
  BulkActionBar,
  DataTable,
  LoadingOrbit,
  Text,
  Flex,
  Card,
  Button,
  useToast,
  Select,
  DateRangePicker,
  TagGroup,
  TextField,
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogFooter,
  MagnifyingGlass,
  Tooltip,
  TooltipTrigger,
  TagBadge,
  type SelectOption,
} from '@chatsdet/ui';
import { Bug as BugIcon, Download, CheckCircle, CircleNotch } from '@phosphor-icons/react';
import { API_URL } from '../../../config';
import { usePropertyCredentialSummaries } from '../../../hooks/usePropertyMetadata';
import type { QaReportStatus } from '../qaStatus';
import { CredentialSelect } from '../../components/CredentialSelect';
import { useCanonicalPages } from '../../../hooks/useCanonicalPages';
import type { DateRangeValue } from '../../../utils/dateRange';
import {
  toInclusiveIsoRange,
  isRangeSelected,
  formatDateRangeDisplay,
  parseIsoToDateRange,
} from '../../../utils/dateRange';

type CanonicalPageListItem = {
  id: string;
  title: string | null;
  url: string;
  firstSeenAt: string;
  lastSeenAt: string;
  qaExcluded?: boolean;
  latestQaReport?: {
    id: string;
    status: QaReportStatus;
    summary?: string | null;
    issuesFoundCount?: number | null;
    testsRunCount?: number | null;
    testsCompletedCount?: number | null;
    completedAt?: string | null;
    startedAt?: string | null;
  } | null;
};

type CanonicalPagesResponse = {
  pages: CanonicalPageListItem[];
  pagination: {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
  };
};

type BulkQaRequest =
  | {
      canonicalPageIds: string[];
    }
  | {
      selectAll: true;
      filters?: {
        search?: string;
        qaStatus?: Exclude<QaStatusFilterValue, 'all'>;
        lastSeenStart?: string;
        lastSeenEnd?: string;
        credentialId?: string;
      };
      excludeCanonicalPageIds?: string[];
    };

type BulkQaResponse = {
  ok: boolean;
  queuedCount: number;
  jobs: Array<{
    canonicalPageId: string;
    jobId: string;
    brokerJobId: string | null;
    qaReportId: string;
  }>;
  error?: string;
};

type RelocationSuggestion = {
  id: string;
  canonicalPage: {
    id: string;
    url: string;
    title: string | null;
    status: string;
  };
  detectedByQaReportId?: string | null;
  detectionReason: string;
  candidateUrl?: string | null;
  candidateCanonicalPageId?: string | null;
  candidateCanonicalPage?: {
    id: string;
    url: string;
    title: string | null;
  } | null;
  confidence?: number | null;
  outcome?: 'moved' | 'removed' | 'unknown' | null;
  createdAt: string;
};

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const DEFAULT_PAGE_SIZE = 25;
type QaStatusFilterValue = 'all' | QaReportStatus | 'not_run';

const QA_STATUS_OPTIONS: SelectOption[] = [
  { id: 'all', label: 'All QA statuses' },
  { id: 'issues_found', label: 'Issues found' },
  { id: 'no_issues', label: 'No issues' },
  { id: 'failed', label: 'Failed' },
  { id: 'running', label: 'Running' },
  { id: 'pending', label: 'Pending' },
  { id: 'not_run', label: 'Not run' },
];

const QA_STATUS_LABELS: Record<Exclude<QaStatusFilterValue, 'all'>, string> = {
  issues_found: 'Issues found',
  no_issues: 'No issues',
  failed: 'Failed',
  running: 'Running',
  pending: 'Pending',
  not_run: 'Not run',
};

type CanonicalPageFilterSnapshot = {
  propertyId?: string | null;
  search?: string;
  qaStatus?: Exclude<QaStatusFilterValue, 'all'>;
  lastSeenStart?: string;
  lastSeenEnd?: string;
  credentialId?: string;
};

export type PagesTableViewProps = {
  propertyId: string;
  searchParams: PagesSearchParams;
  updateSearchParams: (updates: Partial<PagesSearchParams>, resetPage?: boolean) => void;
  hasRelocationsFeature: boolean;
};

export function PagesTableView({
  propertyId,
  searchParams,
  updateSearchParams,
  hasRelocationsFeature,
}: PagesTableViewProps) {
  const buildHref = useRowLinkBuilder();
  const { toast } = useToast();

  const page = searchParams.page ?? 1;
  const pageSize = searchParams.pageSize ?? DEFAULT_PAGE_SIZE;
  const sortBy = searchParams.sortBy ?? 'lastSeenAt';
  const sortOrder = searchParams.sortOrder ?? 'desc';
  const searchQuery = searchParams.search ?? '';
  const qaStatusFilter: QaStatusFilterValue =
    (searchParams.qaStatus as QaStatusFilterValue) ?? 'all';
  const credentialFilter = searchParams.credentialId ?? null;
  const lastSeenRange = React.useMemo<DateRangeValue>(
    () => parseIsoToDateRange(searchParams.lastSeenStart, searchParams.lastSeenEnd),
    [searchParams.lastSeenStart, searchParams.lastSeenEnd]
  );

  const [searchInput, setSearchInput] = React.useState(searchQuery);
  const [isFilterDialogOpen, setIsFilterDialogOpen] = React.useState(false);

  React.useEffect(() => {
    setSearchInput(searchQuery);
  }, [searchQuery]);

  const [selectedPageIds, setSelectedPageIds] = React.useState<Set<string>>(new Set());
  const [deselectedPageIds, setDeselectedPageIds] = React.useState<Set<string>>(new Set());
  const [selectionScope, setSelectionScope] = React.useState<'page' | 'all' | null>(null);
  const [selectAllSnapshot, setSelectAllSnapshot] =
    React.useState<CanonicalPageFilterSnapshot | null>(null);
  const lastSeenIsoRange = React.useMemo(() => toInclusiveIsoRange(lastSeenRange), [lastSeenRange]);

  const exportUrl = React.useMemo(() => {
    const params = new URLSearchParams();
    if (searchQuery) params.set('search', searchQuery);
    if (qaStatusFilter !== 'all') params.set('qaStatus', qaStatusFilter);
    if (lastSeenIsoRange.start) params.set('lastSeenStart', lastSeenIsoRange.start);
    if (lastSeenIsoRange.end) params.set('lastSeenEnd', lastSeenIsoRange.end);
    if (credentialFilter) params.set('credentialId', credentialFilter);
    const query = params.toString();
    return `${API_URL}/properties/${propertyId}/canonical-pages/export${query ? `?${query}` : ''}`;
  }, [propertyId, searchQuery, qaStatusFilter, lastSeenIsoRange, credentialFilter]);

  const [relocationSelections, setRelocationSelections] = React.useState<
    Record<string, { selectedKey: string | null; newUrl: string }>
  >({});

  const credentialsQuery = usePropertyCredentialSummaries(propertyId);
  const canonicalPagesLookup = useCanonicalPages(propertyId, { pageSize: 500 });

  const pendingRelocationsQuery = useQuery<{ suggestions: RelocationSuggestion[] }>({
    queryKey: ['pending-relocations', propertyId],
    enabled: !!propertyId && hasRelocationsFeature,
    queryFn: async () => {
      const res = await fetch(
        `${API_URL}/properties/${propertyId}/canonical-pages/relocations/pending`,
        { credentials: 'include' }
      );
      if (!res.ok) {
        throw new Error('Failed to load pending relocations');
      }
      return (await res.json()) as { suggestions: RelocationSuggestion[] };
    },
    placeholderData: (previous) => previous,
  });

  const canonicalPagesQuery = useQuery<CanonicalPagesResponse>({
    queryKey: [
      'canonical-pages',
      propertyId,
      page,
      pageSize,
      sortBy,
      sortOrder,
      searchQuery,
      qaStatusFilter,
      lastSeenIsoRange.start ?? '',
      lastSeenIsoRange.end ?? '',
      credentialFilter ?? '',
    ],
    queryFn: async (): Promise<CanonicalPagesResponse> => {
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize),
      });
      if (sortBy) params.set('sortBy', sortBy);
      if (sortOrder) params.set('sortOrder', sortOrder);
      if (searchQuery) params.set('search', searchQuery);
      if (qaStatusFilter !== 'all') params.set('qaStatus', qaStatusFilter);
      if (lastSeenIsoRange.start) params.set('lastSeenStart', lastSeenIsoRange.start);
      if (lastSeenIsoRange.end) params.set('lastSeenEnd', lastSeenIsoRange.end);
      if (credentialFilter) params.set('credentialId', credentialFilter);
      const res = await fetch(`${API_URL}/properties/${propertyId}/canonical-pages?${params}`, {
        credentials: 'include',
      });
      if (!res.ok) {
        throw new Error('Failed to load canonical pages');
      }
      return (await res.json()) as CanonicalPagesResponse;
    },
    enabled: !!propertyId,
    placeholderData: (previousData) => previousData,
  });

  // Debounce search
  React.useEffect(() => {
    const handle = window.setTimeout(() => {
      const trimmed = searchInput.trim();
      if (trimmed !== searchQuery) {
        updateSearchParams({ search: trimmed || undefined }, true);
      }
    }, 350);
    return () => window.clearTimeout(handle);
  }, [searchInput, searchQuery, updateSearchParams]);

  const normalizedFilters = React.useMemo<CanonicalPageFilterSnapshot>(
    () => ({
      propertyId: propertyId ?? null,
      search: searchQuery ? searchQuery : undefined,
      qaStatus: qaStatusFilter !== 'all' ? qaStatusFilter : undefined,
      lastSeenStart: lastSeenIsoRange.start ?? undefined,
      lastSeenEnd: lastSeenIsoRange.end ?? undefined,
      credentialId: credentialFilter ?? undefined,
    }),
    [
      credentialFilter,
      lastSeenIsoRange.end,
      lastSeenIsoRange.start,
      propertyId,
      qaStatusFilter,
      searchQuery,
    ]
  );

  const clearSelection = React.useCallback(() => {
    setSelectedPageIds(new Set());
    setDeselectedPageIds(new Set());
    setSelectionScope(null);
    setSelectAllSnapshot(null);
  }, []);

  React.useEffect(() => {
    clearSelection();
  }, [clearSelection, normalizedFilters]);

  const canonicalPageOptions: SelectOption[] = React.useMemo(
    () =>
      (canonicalPagesLookup.data ?? []).map((p) => ({
        id: p.id,
        label: p.title ? `${p.title} (${p.url})` : p.url,
      })),
    [canonicalPagesLookup.data]
  );

  const columns = React.useMemo<ColumnDef<StockFeatures, CanonicalPageListItem>[]>(
    () => [
      {
        id: 'title',
        accessorKey: 'title',
        header: 'Page',
        enableSorting: true,
        cell: ({ row }: { row: { original: CanonicalPageListItem } }) => (
          <Flex align="center" gap="xs" className="min-w-0">
            <Text
              size="sm"
              weight="medium"
              className="truncate"
              title={row.original.title || 'Untitled page'}
            >
              {row.original.title || 'Untitled page'}
            </Text>
            {row.original.qaExcluded && (
              <TagBadge name="QA excluded" color="gray" hideDot className="shrink-0" />
            )}
          </Flex>
        ),
        meta: { flex: 0.5 },
      },
      {
        id: 'path',
        accessorKey: 'url',
        header: 'Path',
        enableSorting: true,
        cell: ({ row }: { row: { original: CanonicalPageListItem } }) => (
          <TooltipTrigger delay={100} offset={8}>
            <Text size="xs" className="text-muted-foreground font-mono truncate cursor-default">
              {getUrlPath(row.original.url)}
            </Text>
            <Tooltip>{row.original.url}</Tooltip>
          </TooltipTrigger>
        ),
        meta: { flex: 0.4 },
      },
      {
        id: 'issuesCount',
        header: 'Issues',
        cell: ({ row }: { row: { original: CanonicalPageListItem } }) => {
          const report = row.original.latestQaReport;
          if (!report) {
            return (
              <Text size="xs" className="text-muted-foreground">
                —
              </Text>
            );
          }
          const bugs = report.issuesFoundCount ?? 0;
          const tooltipText = bugs === 1 ? '1 issue found' : `${bugs} issues found`;
          return (
            <TooltipTrigger delay={100} offset={8}>
              <span className="inline-flex items-center gap-1 cursor-default">
                <BugIcon
                  size={14}
                  className={bugs > 0 ? 'text-destructive' : 'text-muted-foreground'}
                />
                <Text size="xs" className={bugs > 0 ? 'text-destructive' : 'text-muted-foreground'}>
                  {bugs}
                </Text>
              </span>
              <Tooltip>{tooltipText}</Tooltip>
            </TooltipTrigger>
          );
        },
        meta: { flex: 0.08 },
      },
      {
        id: 'testsCount',
        header: 'Tests',
        cell: ({ row }: { row: { original: CanonicalPageListItem } }) => {
          const report = row.original.latestQaReport;
          if (!report) {
            return (
              <Text size="xs" className="text-muted-foreground">
                —
              </Text>
            );
          }
          const total = report.testsRunCount ?? 0;
          const completed = report.testsCompletedCount ?? 0;
          const isRunning = report.status === 'running' || report.status === 'pending';
          const tooltipText =
            completed === 1
              ? `1 test completed of ${total}`
              : `${completed} tests completed of ${total}`;
          return (
            <TooltipTrigger delay={100} offset={8}>
              <span className="inline-flex items-center gap-1 cursor-default">
                {isRunning ? (
                  <CircleNotch size={14} className="text-primary animate-spin" />
                ) : (
                  <CheckCircle size={14} className="text-muted-foreground" />
                )}
                <Text size="xs" className="text-muted-foreground">
                  {completed}/{total}
                </Text>
              </span>
              <Tooltip>{isRunning ? 'Test in progress' : tooltipText}</Tooltip>
            </TooltipTrigger>
          );
        },
        meta: { flex: 0.1 },
      },
      {
        id: 'lastSeenAt',
        accessorKey: 'lastSeenAt',
        header: 'Last Seen',
        enableSorting: true,
        cell: ({ row }: { row: { original: CanonicalPageListItem } }) => {
          const lastSeen = row.original.lastSeenAt;
          if (!lastSeen) {
            return (
              <Text size="xs" className="text-muted-foreground">
                —
              </Text>
            );
          }
          return (
            <TooltipTrigger delay={100} offset={8}>
              <Text size="xs" className="text-muted-foreground cursor-default">
                {formatDate(lastSeen)}
              </Text>
              <Tooltip>{new Date(lastSeen).toLocaleString()}</Tooltip>
            </TooltipTrigger>
          );
        },
        meta: { flex: 0.2 },
      },
    ],
    []
  );

  const pageItems = canonicalPagesQuery.data?.pages ?? [];
  const paginationInfo = canonicalPagesQuery.data?.pagination ?? null;
  const totalItems =
    typeof paginationInfo?.totalItems === 'number' ? paginationInfo.totalItems : pageItems.length;

  const rowSelection = React.useMemo<RowSelectionState>(() => {
    const state: RowSelectionState = {};
    if (selectionScope === 'all') {
      pageItems.forEach((p) => {
        if (!deselectedPageIds.has(p.id)) state[p.id] = true;
      });
      return state;
    }
    pageItems.forEach((p) => {
      if (selectedPageIds.has(p.id)) state[p.id] = true;
    });
    return state;
  }, [deselectedPageIds, pageItems, selectedPageIds, selectionScope]);

  const deselectedCount = selectionScope === 'all' ? deselectedPageIds.size : 0;
  const selectedCount =
    selectionScope === 'all' ? Math.max(0, totalItems - deselectedCount) : selectedPageIds.size;
  const canSelectAllResults =
    selectionScope !== 'all' && selectedCount > 0 && totalItems > selectedCount;
  const resolvedSelectionMode: 'page' | 'all' = selectionScope === 'all' ? 'all' : 'page';

  const handleRowSelectionChange = React.useCallback(
    (updater: RowSelectionState | ((prev: RowSelectionState) => RowSelectionState)) => {
      const base = rowSelection;
      const nextState = typeof updater === 'function' ? updater(base) : updater;

      if (selectionScope === 'all') {
        const nextDeselected = new Set(deselectedPageIds);
        pageItems.forEach(({ id }) => {
          const wasSelected = Boolean(base[id]);
          const isSelected = Boolean(nextState[id]);
          if (wasSelected === isSelected) return;
          if (isSelected) {
            nextDeselected.delete(id);
          } else {
            nextDeselected.add(id);
          }
        });

        if (totalItems > 0 && nextDeselected.size >= totalItems) {
          clearSelection();
          return;
        }
        setDeselectedPageIds(nextDeselected);
        return;
      }

      const nextSelected = new Set(selectedPageIds);
      pageItems.forEach(({ id }) => {
        const isSelected = Boolean(nextState[id]);
        if (isSelected) {
          nextSelected.add(id);
        } else {
          nextSelected.delete(id);
        }
      });
      setSelectedPageIds(nextSelected);
      setSelectionScope(nextSelected.size > 0 ? 'page' : null);
    },
    [
      clearSelection,
      deselectedPageIds,
      pageItems,
      rowSelection,
      selectedPageIds,
      selectionScope,
      totalItems,
    ]
  );

  const handleSelectAllResults = React.useCallback(() => {
    if (!canSelectAllResults) return;
    setSelectionScope('all');
    setSelectedPageIds(new Set());
    setDeselectedPageIds(new Set());
    setSelectAllSnapshot(normalizedFilters);
  }, [canSelectAllResults, normalizedFilters]);

  const handleServerSortChange = React.useCallback(
    (nextSortBy?: string, nextSortOrder?: 'asc' | 'desc') => {
      updateSearchParams(
        { sortBy: nextSortBy ?? 'lastSeenAt', sortOrder: nextSortOrder ?? 'desc' },
        true
      );
    },
    [updateSearchParams]
  );

  const runQaBulkMutation = useMutation({
    mutationFn: async (payload: BulkQaRequest): Promise<BulkQaResponse> => {
      const response = await fetch(
        `${API_URL}/properties/${propertyId}/canonical-pages/bulk-qa-tests`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );
      const data = (await response.json()) as BulkQaResponse;
      if (!response.ok || !data.ok) {
        throw new Error(data.error ?? 'Failed to queue QA tests');
      }
      return data;
    },
  });

  const resolveRelocationMutation = useMutation({
    mutationFn: async (input: {
      suggestionId: string;
      canonicalPageId: string;
      action: 'move' | 'remove' | 'reject';
      targetCanonicalPageId?: string | null;
      newUrl?: string | null;
    }) => {
      const res = await fetch(
        `${API_URL}/properties/${propertyId}/canonical-pages/${input.canonicalPageId}/relocations/${input.suggestionId}/resolve`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            action: input.action,
            targetCanonicalPageId: input.targetCanonicalPageId ?? undefined,
            newUrl: input.newUrl ?? undefined,
          }),
        }
      );
      const data = (await res.json()) as { ok?: boolean; error?: string };
      if (!res.ok || data.ok === false) {
        throw new Error(data.error ?? 'Failed to resolve relocation');
      }
      return data;
    },
    onSuccess: () => {
      void pendingRelocationsQuery.refetch();
      void canonicalPagesQuery.refetch();
      void canonicalPagesLookup.refetch();
      setRelocationSelections({});
    },
  });

  const handleRunQaSelected = React.useCallback(async () => {
    if (selectedCount === 0) return;

    const payload: BulkQaRequest =
      selectionScope === 'all'
        ? {
            selectAll: true as const,
            filters: {
              search: (selectAllSnapshot ?? normalizedFilters).search,
              qaStatus: (selectAllSnapshot ?? normalizedFilters).qaStatus,
              lastSeenStart: (selectAllSnapshot ?? normalizedFilters).lastSeenStart,
              lastSeenEnd: (selectAllSnapshot ?? normalizedFilters).lastSeenEnd,
              credentialId: (selectAllSnapshot ?? normalizedFilters).credentialId,
            },
            excludeCanonicalPageIds:
              deselectedPageIds.size > 0 ? Array.from(deselectedPageIds) : undefined,
          }
        : { canonicalPageIds: Array.from(selectedPageIds) };

    try {
      const result = await runQaBulkMutation.mutateAsync(payload);
      clearSelection();
      await canonicalPagesQuery.refetch();
      toast.success(
        result.queuedCount === 1
          ? 'Queued QA for 1 page'
          : `Queued QA for ${result.queuedCount} pages`
      );
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to queue QA tests');
    }
  }, [
    canonicalPagesQuery,
    clearSelection,
    deselectedPageIds,
    normalizedFilters,
    runQaBulkMutation,
    selectedCount,
    selectedPageIds,
    selectAllSnapshot,
    selectionScope,
    toast,
  ]);

  const getRelocationSelection = React.useCallback(
    (suggestion: RelocationSuggestion) => {
      const existing = relocationSelections[suggestion.id];
      if (existing) return existing;
      const selectedKey =
        suggestion.candidateCanonicalPageId ?? (suggestion.candidateUrl ? '__new__' : null);
      const newUrl = suggestion.candidateUrl ?? '';
      return { selectedKey, newUrl };
    },
    [relocationSelections]
  );

  const updateRelocationSelection = React.useCallback(
    (suggestionId: string, selectedKey: string | null, newUrl?: string) => {
      setRelocationSelections((prev) => ({
        ...prev,
        [suggestionId]: {
          selectedKey,
          newUrl: newUrl ?? prev[suggestionId]?.newUrl ?? '',
        },
      }));
    },
    []
  );

  const handleResolveMove = React.useCallback(
    async (suggestion: RelocationSuggestion) => {
      const selection = getRelocationSelection(suggestion);
      const selectedKey = selection.selectedKey;
      const newUrl = selection.newUrl?.trim() ?? '';

      if (!selectedKey && !newUrl) {
        toast.error('Select a canonical page or provide a new URL');
        return;
      }

      if (selectedKey === '__new__') {
        if (!newUrl) {
          toast.error('Enter a destination URL');
          return;
        }
        await resolveRelocationMutation.mutateAsync({
          suggestionId: suggestion.id,
          canonicalPageId: suggestion.canonicalPage.id,
          action: 'move',
          newUrl,
        });
      } else {
        await resolveRelocationMutation.mutateAsync({
          suggestionId: suggestion.id,
          canonicalPageId: suggestion.canonicalPage.id,
          action: 'move',
          targetCanonicalPageId: selectedKey,
        });
      }
      toast.success('Relocation updated');
    },
    [getRelocationSelection, resolveRelocationMutation, toast]
  );

  const handleResolveRemove = React.useCallback(
    async (suggestion: RelocationSuggestion) => {
      await resolveRelocationMutation.mutateAsync({
        suggestionId: suggestion.id,
        canonicalPageId: suggestion.canonicalPage.id,
        action: 'remove',
      });
      toast.success('Marked as removed');
    },
    [resolveRelocationMutation, toast]
  );

  const handleRejectRelocation = React.useCallback(
    async (suggestion: RelocationSuggestion) => {
      await resolveRelocationMutation.mutateAsync({
        suggestionId: suggestion.id,
        canonicalPageId: suggestion.canonicalPage.id,
        action: 'reject',
      });
      toast.success('Suggestion rejected');
    },
    [resolveRelocationMutation, toast]
  );

  const hasLastSeenFilter = isRangeSelected(lastSeenRange);

  const activeFilterChips = React.useMemo(() => {
    const chips: Array<{ id: string; label: string }> = [];
    if (qaStatusFilter !== 'all') {
      chips.push({ id: 'qaStatus', label: `QA: ${QA_STATUS_LABELS[qaStatusFilter]}` });
    }
    const dateLabel = formatDateRangeDisplay(lastSeenRange);
    if (dateLabel) {
      chips.push({ id: 'lastSeen', label: `Last seen: ${dateLabel}` });
    }
    if (credentialFilter) {
      const credential = credentialsQuery.data?.items.find((c) => c.id === credentialFilter);
      chips.push({ id: 'credential', label: `Credential: ${credential?.name ?? 'Unknown'}` });
    }
    return chips;
  }, [credentialFilter, credentialsQuery.data?.items, lastSeenRange, qaStatusFilter]);

  const filtersCount = activeFilterChips.length;
  const hasActiveFilters = filtersCount > 0;

  const handleFilterRemove = React.useCallback(
    (chipId: string) => {
      switch (chipId) {
        case 'qaStatus':
          updateSearchParams({ qaStatus: undefined }, true);
          break;
        case 'lastSeen':
          updateSearchParams({ lastSeenStart: undefined, lastSeenEnd: undefined }, true);
          break;
        case 'credential':
          updateSearchParams({ credentialId: undefined }, true);
          break;
      }
    },
    [updateSearchParams]
  );

  const handleResetFilters = React.useCallback(() => {
    updateSearchParams(
      {
        qaStatus: undefined,
        lastSeenStart: undefined,
        lastSeenEnd: undefined,
        credentialId: undefined,
      },
      true
    );
  }, [updateSearchParams]);

  const handleClearLastSeen = React.useCallback(() => {
    updateSearchParams({ lastSeenStart: undefined, lastSeenEnd: undefined }, true);
  }, [updateSearchParams]);

  const resultsLabel = React.useMemo(() => {
    const resultsWord = totalItems === 1 ? 'result' : 'results';
    if (hasActiveFilters) {
      const filtersWord = filtersCount === 1 ? 'filter' : 'filters';
      return `${totalItems} ${resultsWord} · ${filtersCount} ${filtersWord}`;
    }
    return `${totalItems} ${resultsWord}`;
  }, [filtersCount, hasActiveFilters, totalItems]);

  if (canonicalPagesQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingOrbit size="lg" />
      </div>
    );
  }

  if (canonicalPagesQuery.isError) {
    return (
      <Card className="p-6 border-dashed border text-center">
        <Text weight="medium">Unable to load canonical pages</Text>
        <Text size="sm" className="text-muted-foreground mt-1">
          Please try again.
        </Text>
      </Card>
    );
  }

  const response = canonicalPagesQuery.data;
  const pagination = response?.pagination ?? paginationInfo;
  const pageCount = pagination?.totalPages ?? 0;
  const noResults = (pagination?.totalItems ?? 0) === 0;
  const pendingSuggestions = pendingRelocationsQuery.data?.suggestions ?? [];

  const filterDialogTrigger = (
    <DialogTrigger isOpen={isFilterDialogOpen} onOpenChange={setIsFilterDialogOpen} size="lg">
      <Button variant={hasActiveFilters ? 'primary' : 'secondary'}>
        {hasActiveFilters ? `Edit filters (${filtersCount})` : 'Add filter'}
      </Button>
      <Dialog
        title="Filter pages"
        description="Choose attributes to narrow which pages appear in the table."
      >
        <DialogContent className="space-y-6">
          <div className="space-y-3">
            <Text size="sm" className="font-semibold text-foreground">
              Time
            </Text>
            <DateRangePicker
              label="Last seen"
              value={lastSeenRange}
              onChange={(range) => {
                const isoRange = toInclusiveIsoRange(range);
                updateSearchParams(
                  { lastSeenStart: isoRange.start, lastSeenEnd: isoRange.end },
                  true
                );
              }}
              granularity="day"
            />
            <Button
              variant="ghost"
              size="sm"
              onPress={handleClearLastSeen}
              isDisabled={!hasLastSeenFilter}
              className="self-start"
            >
              Clear date range
            </Button>
          </div>
          <div className="space-y-4">
            <Text size="sm" className="font-semibold text-foreground">
              Attributes
            </Text>
            <Select
              label="QA status"
              items={QA_STATUS_OPTIONS}
              selectedKey={qaStatusFilter}
              onSelectionChange={(key) => {
                if (key) {
                  updateSearchParams(
                    { qaStatus: key === 'all' ? undefined : (key as string) },
                    true
                  );
                }
              }}
            />
            <CredentialSelect
              propertyId={propertyId}
              selectedCredentialId={credentialFilter}
              onSelect={(id) => updateSearchParams({ credentialId: id ?? undefined }, true)}
              label="Last seen credential"
            />
          </div>
        </DialogContent>
        <DialogFooter>
          <Button variant="outline" onPress={handleResetFilters}>
            Reset filters
          </Button>
          <Button variant="primary" onPress={() => setIsFilterDialogOpen(false)}>
            Done
          </Button>
        </DialogFooter>
      </Dialog>
    </DialogTrigger>
  );

  return (
    <div className="space-y-6">
      {hasRelocationsFeature && pendingSuggestions.length ? (
        <Card className="p-4 space-y-3">
          <Flex align="center" justify="between" wrap>
            <Text weight="medium">Pending page relocations</Text>
            <Text size="sm" className="text-muted-foreground">
              Review and confirm suggested moves or removals.
            </Text>
          </Flex>
          <div className="space-y-3">
            {pendingSuggestions.map((suggestion) => {
              const selection = getRelocationSelection(suggestion);
              const isRemoval =
                suggestion.outcome === 'removed' ||
                (!suggestion.candidateCanonicalPageId && !suggestion.candidateUrl);
              const baseOptions = canonicalPageOptions.filter(
                (opt) => opt.id !== suggestion.canonicalPage.id
              );
              if (
                suggestion.candidateCanonicalPageId &&
                !baseOptions.find((opt) => opt.id === suggestion.candidateCanonicalPageId)
              ) {
                baseOptions.unshift({
                  id: suggestion.candidateCanonicalPageId,
                  label:
                    suggestion.candidateCanonicalPage?.title ??
                    suggestion.candidateCanonicalPage?.url ??
                    suggestion.candidateCanonicalPageId,
                });
              }
              const selectItems: SelectOption[] = [
                ...baseOptions,
                { id: '__new__', label: 'Use a new URL' },
              ];
              const showNewUrlInput =
                selection.selectedKey === '__new__' ||
                (!selection.selectedKey && !!suggestion.candidateUrl);

              return (
                <div
                  key={suggestion.id}
                  className="rounded-md border px-3 py-3 space-y-2 bg-muted/50"
                >
                  <Flex justify="between" align="start" wrap gap="sm">
                    <div>
                      <Text weight="medium">
                        {suggestion.canonicalPage.title || 'Untitled page'}
                      </Text>
                      <Text size="sm" className="text-muted-foreground">
                        {getUrlPath(suggestion.canonicalPage.url)}
                      </Text>
                    </div>
                    <TagBadge
                      name={<>Detected: {suggestion.detectionReason}</>}
                      color="gray"
                      hideDot
                    />
                  </Flex>
                  {suggestion.candidateCanonicalPage ? (
                    <Text size="sm">
                      Suggested match: {suggestion.candidateCanonicalPage.title || 'Untitled'} (
                      {getUrlPath(suggestion.candidateCanonicalPage.url)})
                    </Text>
                  ) : suggestion.candidateUrl ? (
                    <Text size="sm">Suggested new URL: {getUrlPath(suggestion.candidateUrl)}</Text>
                  ) : null}
                  {isRemoval ? (
                    <Flex gap="sm" wrap>
                      <Button
                        variant="primary"
                        size="sm"
                        onPress={() => void handleResolveRemove(suggestion)}
                        isDisabled={resolveRelocationMutation.isPending}
                      >
                        {resolveRelocationMutation.isPending ? 'Saving...' : 'Mark removed'}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onPress={() => void handleRejectRelocation(suggestion)}
                        isDisabled={resolveRelocationMutation.isPending}
                      >
                        Reject
                      </Button>
                    </Flex>
                  ) : (
                    <div className="space-y-2">
                      <Select
                        label="Merge into"
                        items={selectItems}
                        selectedKey={selection.selectedKey ?? undefined}
                        onSelectionChange={(key) =>
                          updateRelocationSelection(
                            suggestion.id,
                            key ? String(key) : null,
                            selection.newUrl
                          )
                        }
                        placeholder="Choose a canonical page"
                      />
                      {showNewUrlInput ? (
                        <TextField
                          label="Destination URL"
                          value={selection.newUrl}
                          onChange={(value) =>
                            updateRelocationSelection(suggestion.id, selection.selectedKey, value)
                          }
                          placeholder="https://example.com/new-page"
                        />
                      ) : null}
                      <Flex gap="sm" wrap>
                        <Button
                          variant="primary"
                          size="sm"
                          onPress={() => void handleResolveMove(suggestion)}
                          isDisabled={resolveRelocationMutation.isPending}
                        >
                          {resolveRelocationMutation.isPending ? 'Saving...' : 'Confirm'}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onPress={() => void handleRejectRelocation(suggestion)}
                          isDisabled={resolveRelocationMutation.isPending}
                        >
                          Reject
                        </Button>
                      </Flex>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      ) : null}

      <Flex align="center" gap="sm" wrap justify="between">
        <TextField
          aria-label="Search pages"
          placeholder="Search pages"
          value={searchInput}
          onChange={setSearchInput}
          leftIcon={<MagnifyingGlass size={16} />}
          className="w-full max-w-xl min-w-[260px]"
        />
        <Flex gap="sm" align="center" className="flex-1 justify-end min-w-[200px]">
          {hasActiveFilters ? (
            <Button variant="ghost" size="sm" onPress={handleResetFilters}>
              Reset
            </Button>
          ) : null}
          {filterDialogTrigger}
        </Flex>
      </Flex>

      <Flex align="center" justify="between" gap="md" wrap>
        <Flex align="center" gap="sm" wrap className="flex-1 min-w-[200px]">
          <Text size="sm" className="text-muted-foreground font-medium whitespace-nowrap">
            Active filters:
          </Text>
          {hasActiveFilters ? (
            <TagGroup
              aria-label="Active filters"
              items={activeFilterChips}
              onRemove={handleFilterRemove}
              className="flex-1"
            />
          ) : (
            <Text size="sm" className="text-muted-foreground">
              None
            </Text>
          )}
        </Flex>
        <Flex align="center" gap="sm">
          <Text size="sm" className="text-muted-foreground whitespace-nowrap">
            {resultsLabel}
          </Text>
          {exportUrl ? (
            <Button
              variant="icon"
              size="sm"
              iconOnly
              tooltip="Export CSV"
              onPress={() => window.open(exportUrl, '_blank')}
            >
              <Download size={16} />
            </Button>
          ) : null}
        </Flex>
      </Flex>

      <div className="space-y-3">
        {noResults ? (
          <Card className="p-6 border-dashed border text-center">
            <Text weight="medium">
              {searchQuery || hasActiveFilters
                ? 'No pages match your search or filters'
                : 'No canonical pages yet'}
            </Text>
            <Text size="sm" className="text-muted-foreground mt-1">
              {searchQuery || hasActiveFilters
                ? 'Adjust your search or filters to see more pages.'
                : 'Pages are added automatically as this application is tested.'}
            </Text>
          </Card>
        ) : null}

        <BulkActionBar
          selectedCount={selectedCount}
          itemLabel="page"
          selectionMode={resolvedSelectionMode}
          deselectedCount={deselectedCount}
          totalItems={totalItems}
          onSelectAll={canSelectAllResults ? handleSelectAllResults : undefined}
          onClearSelection={clearSelection}
          actions={[
            {
              label: 'Run QA',
              loadingLabel: 'Queuing...',
              variant: 'primary',
              isLoading: runQaBulkMutation.isPending,
              onPress: handleRunQaSelected,
            },
          ]}
        />

        <DataTable
          columns={columns}
          data={pageItems}
          aria-label="Canonical pages table"
          getRowId={(item) => item.id}
          enableRowSelection
          rowSelection={rowSelection}
          onRowSelectionChange={handleRowSelectionChange}
          getRowHref={(row) =>
            buildHref({
              to: '/pages/$propertyId/canonical/$canonicalPageId',
              params: { propertyId, canonicalPageId: row.id },
            })
          }
          serverPagination={
            pagination
              ? {
                  page,
                  pageSize,
                  pageCount: Math.max(pageCount, 1),
                  totalItems: pagination.totalItems,
                  onPageChange: (nextPage: number) => updateSearchParams({ page: nextPage }),
                  onPageSizeChange: (nextPageSize: number) => {
                    updateSearchParams({ pageSize: nextPageSize }, true);
                  },
                  pageSizeOptions: PAGE_SIZE_OPTIONS,
                  isLoading: canonicalPagesQuery.isFetching,
                }
              : undefined
          }
          serverSorting={{
            sortBy,
            sortOrder,
            onSortChange: handleServerSortChange,
          }}
        />
      </div>
    </div>
  );
}

function formatDate(value: string | null | undefined) {
  if (!value) return 'Unknown';
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(value));
  } catch {
    return value;
  }
}

function getUrlPath(url: string): string {
  try {
    return new URL(url).pathname;
  } catch {
    return url;
  }
}
